/**
 * The screens F4 routes to.
 *
 * F4 owns the **routes and the frames**; the content of each belongs to a later module and is
 * named on every placeholder, so nothing here can be mistaken for a finished screen. What is
 * real here is the routing behaviour the acceptance criteria test: the `/boards/:id` view
 * resolution (R2), the URL-driven item panel (R3), the activity drawer (R5), the inbox modal
 * (R4) and the 403 / 404 states (N1).
 */
import {
  BottomActionBar,
  Heading,
  PageEmptyState,
  PageErrorState,
  PageSkeleton,
  Text,
} from '@elizmonday/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, useLocation, useNavigate, useParams, useRouteError } from 'react-router';
import { useBoardSummary, useBootstrap } from '../modules/shell/bootstrap';
import { useShortcut } from '../modules/shell/shortcuts';
import { usePreference, usePreferencesHydrated } from '../modules/shell/preferences';
import { boardViewUrl, isValidId, itemInViewUrl, workspaceUrl } from '../modules/shell/urls';
import type { ViewId } from '@elizmonday/shared';
import { useBoardSource } from '../modules/board-view/data/useBoardSource';
import { BoardViewRealtime } from '../modules/board-view/data/BoardViewRealtime';
import { BoardHeader } from '../modules/boards/BoardHeader';
import { ViewTabsBar } from '../modules/views/ViewTabsBar';
import { BoardWidgetView } from '../modules/dashboards';
import { ComingSoonView } from '../modules/views/ComingSoonView';
import { resolveViewKind } from '../modules/views/resolve-kind';
/* M4 V2 / V4 / V7 — the three renderers round 5u built; each takes the board's data source. */
import { KanbanView } from '../modules/views/kanban/KanbanView';
import { CalendarView } from '../modules/views/calendar/CalendarView';
import { GalleryView } from '../modules/views/gallery/GalleryView';
import { GanttView } from '../modules/views/gantt/GanttView';
import { TimelineView } from '../modules/views/timeline/TimelineView';
import { WorkloadView } from '../modules/views/workload/WorkloadView';
import { useBoardViews } from '../modules/boards/hooks';
import { useBoardChromeRealtime } from '../modules/boards/useBoardChromeRealtime';
import { BoardFormView } from '../modules/forms/BoardFormView';
import { RemovedBoardsScreen } from '../modules/boards/RemovedBoardsScreen';
import { WorkspaceHome as WorkspaceHomeScreen } from '../modules/boards/WorkspaceHome';
import { TableViewProvider } from '../modules/board-view/table/context';
import { TableView } from '../modules/board-view/table/TableView';
import { BoardActivityPanel } from '../modules/item-card/BoardActivityPanel';
import { ItemCard, type ItemCardTab } from '../modules/item-card/ItemCard';
import { useItemCardSource } from '../modules/item-card/useItemCardSource';
import { useTRPCClient } from '../lib/trpc';
import { invalidateUnreadCounts, useUnreadQueryKeys } from '../app/unread';
import { MyWorkPage, createMyWorkSource, type MyWorkTrpc } from '../modules/my-work';
import '../modules/my-work/my-work.css';
/*
 * M10 · Tasks (round T2). The stylesheet is imported at the route module rather than inside the
 * page, because the **rail badge** it styles (`.ez-rail__badge`) is painted by the shell on every
 * screen, not only on `/tasks` (`modules/tasks/TasksRailEntry.tsx`).
 */
import {
  TasksPage,
  createTasksSource,
  tabFromPath,
  taskItemUrl,
  useTasksRealtime,
  TASK_TAB_PATHS,
  type TasksTrpc,
} from '../modules/tasks';
import '../modules/tasks/tasks.css';
import {
  InboxScreen,
  NotificationSettings,
  createNotificationsSource,
  useNotificationsRealtime,
  type InboxTabId,
  type NotificationsTrpc,
} from '../modules/notifications';
import '../modules/notifications/notifications.css';

/** R1: a non-numeric id renders the 404 state without a network request. */
function useValidatedParams(names: string[]): Record<string, string> | null {
  const params = useParams();
  const values: Record<string, string> = {};
  for (const name of names) {
    const value = params[name];
    if (!isValidId(value)) return null;
    values[name] = value;
  }
  return values;
}

/** N2: reports to the console, offers a retry and keeps the shell mounted. */
export function RouteErrorBoundary() {
  const error = useRouteError();
  console.error('Route error', error);
  return (
    <PageErrorState
      kind="error"
      action={
        <button type="button" className="ez-profile-menu__item" onClick={() => window.location.reload()}>
          Retry
        </button>
      }
      data-testid="route-error"
    />
  );
}

/**
 * `/` resolves the account home page; the workspace comes from the shell's own preference, and
 * on a cold start (nothing chosen yet) from the account's **default** workspace — T-9h, the
 * same fallback `AppShell` applies, so `/` and the sidebar never disagree.
 */
export function HomeRoute() {
  const [lastWorkspaceId] = usePreference('shell.lastWorkspaceId');
  const hydrated = usePreferencesHydrated();
  const bootstrap = useBootstrap();
  const fallback = bootstrap.data?.workspaces.defaultId ?? bootstrap.data?.workspaces.recent[0]?.id ?? null;
  /*
   * **T-9h.** Wait for the *preferences*, not only for the query: `shell.bootstrap` resolving
   * and the store hydrating are two different moments (the hydration is an effect of the render
   * the data arrives in), and in the render between them `shell.lastWorkspaceId` still reads the
   * store's `null`. Redirecting there sent a user with a stored choice to the account's default
   * workspace — every time, on a browser that had never stored anything locally. A failed
   * bootstrap never hydrates, so the fallback still gets its chance.
   */
  if (!hydrated && !bootstrap.isError) return <PageSkeleton />;
  return <HomeRedirect workspaceId={lastWorkspaceId ?? fallback} />;
}

export function NotFound() {
  const location = useLocation();
  return (
    <PageErrorState
      kind="not-found"
      // The board name is never disclosed on a denial (E8); the path is not a secret.
      hint={`Nothing is routed at ${location.pathname}. We track these errors automatically, but if the problem reoccurs please contact your administrator.`}
      action={<Link to="/">Back to home</Link>}
      data-testid="not-found-panel"
    />
  );
}

/**
 * `/` resolves the account's home page setting (`[as §General > Account]`): `Default` sends the
 * user to their last workspace, then the first one they can read, then an empty state (E1).
 * `Dashboard` and `VibeApps` both fall back to `Default` for now (§7.1, F4-11).
 */
export function HomeRedirect({ workspaceId }: { workspaceId: string | null }) {
  if (workspaceId) return <Navigate to={workspaceUrl(workspaceId)} replace />;
  return (
    <PageEmptyState
      title="No workspace yet"
      hint="Create a workspace to get started."
      data-testid="home-empty"
    />
  );
}

export function WorkspaceHome() {
  const params = useValidatedParams(['workspaceId']);
  if (!params) return <NotFound />;
  return <WorkspaceHomeScreen workspaceId={params['workspaceId']!} />;
}

/** `/trash` and `/archive`: the two account-level destinations of the avatar menu (B1 §7.5). */
export function TrashRoute() {
  return <RemovedBoardsScreen state="deleted" />;
}

export function ArchiveRoute() {
  return <RemovedBoardsScreen state="archived" />;
}

/**
 * R2: `/boards/:id` redirects (`replace`, so Back does not bounce) to
 * `/boards/:id/views/:viewId` using `board.lastViewId`, then the board's default view, then the
 * first view by position. E3: a board with no views renders the implicit default table view.
 *
 * The same component also serves `/boards/:id/pulses/:itemId` — monday's view-less deep link,
 * the one every migrated notification e-mail and every `Item.relative_link` carries (doc 00:
 * a migrated link must keep working). The `pulses/:itemId` suffix therefore has to survive the
 * redirect: without it the URL resolved to the board and the item card never opened.
 */
export function BoardRedirect() {
  const params = useValidatedParams(['boardId']);
  const boardId = params?.['boardId'] ?? null;
  const routeParams = useParams();
  const itemId = routeParams['itemId'];
  const board = useBoardSummary(boardId);
  const [lastViewId, setLastViewId] = usePreference('board.lastViewId');

  if (!params) return <NotFound />;
  if (board.isPending) return <PageSkeleton variant="board" />;
  if (board.isError) return <BoardError error={board.error} />;

  const views = board.data?.views ?? [];
  const stored = lastViewId[params['boardId']!];
  // E2: a stored view that no longer exists falls back and the preference is rewritten.
  const storedStillExists = stored && views.some((view) => view.id === stored);
  const target = storedStillExists
    ? stored
    : (views.find((view) => view.isDefault)?.id ?? views[0]?.id ?? null);

  if (stored && !storedStillExists) {
    const { [params['boardId']!]: _dropped, ...rest } = lastViewId;
    setLastViewId(rest);
  }

  if (!target) return <BoardView implicitDefaultView />;
  return (
    <Navigate
      to={
        isValidId(itemId)
          ? itemInViewUrl(params['boardId']!, target, itemId)
          : boardViewUrl(params['boardId']!, target)
      }
      replace
    />
  );
}

function BoardError({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : '';
  // N1: 403 says who to ask; 404 discloses nothing.
  const forbidden = /not allowed|forbidden/i.test(message);
  return (
    <PageErrorState
      kind={forbidden ? 'forbidden' : 'not-found'}
      data-testid={forbidden ? 'forbidden-panel' : 'not-found-panel'}
    />
  );
}

/**
 * `implicitDefaultView` is set by `BoardRedirect` for the 138 boards that expose no view row
 * at all (E3 / V2): the tabs bar renders the service's synthetic "Main Table" entry and the
 * grid opens on it, so the URL stays `/boards/:id`.
 */
export function BoardView({ implicitDefaultView = false }: { implicitDefaultView?: boolean }) {
  const params = useParams();
  const boardId = params['boardId'];
  const viewId = params['viewId'];
  const itemId = params['itemId'];
  const navigate = useNavigate();
  const location = useLocation();
  const board = useBoardSummary(isValidId(boardId) ? boardId : null);
  const [lastViewId, setLastViewId] = usePreference('board.lastViewId');
  /**
   * Round 3: the product route runs on the **real** api. `/__ui/board` keeps the in-memory
   * fixture source, and neither the table view nor the store can tell the difference —
   * `BoardDataSource` is the whole contract (§5).
   */
  const boardSource = useBoardSource(isValidId(boardId) ? boardId : null);
  /** M4.V1: the tabs bar's rows, reused here to decide which renderer the open view gets. */
  const views = useBoardViews(isValidId(boardId) ? boardId : null);
  /**
   * **P-086.** The board's chrome — the header and the tab bar — is two TanStack Query caches,
   * not the table store, so nothing repainted them when someone else renamed the board or added
   * a view. It lives here rather than in `TableViewProvider` because the chrome is the same on
   * every view kind.
   */
  useBoardChromeRealtime(isValidId(boardId) ? boardId : null);
  /** M1-B7 / M1-B8: the item card and the board log read their own source. */
  const cardSource = useItemCardSource();
  const [selection] = useState<string[]>([]);

  // The last view per board is per-user state, exactly like monday's (§4.2, observed).
  useEffect(() => {
    if (!boardId || !viewId) return;
    if (lastViewId[boardId] === viewId) return;
    setLastViewId({ ...lastViewId, [boardId]: viewId });
  }, [boardId, viewId, lastViewId, setLastViewId]);

  if (!isValidId(boardId)) return <NotFound />;
  if (board.isPending) return <PageSkeleton variant="board" />;
  if (board.isError) return <BoardError error={board.error} />;

  const activityLogOpen = location.pathname.endsWith('/activity_log');
  /**
   * Which kind the open tab is. `null` while the tabs are loading, or when the view is a table:
   * both fall through to the grid, which is what every board opened on before M4.
   */
  const activeKind = resolveViewKind(views.data, viewId ?? null);

  /**
   * §7.1 rule b: the panel and the drawer are URL segments, not state. Closing goes back when
   * the previous entry is ours and replaces with the parent route otherwise.
   */
  const closeSegment = (parent: string) => {
    /*
     * P-323: "ours" means opened **from this route** — the history entry carries `cardParent`.
     * A card reached from Tasks' `Open board ↗`, a notification or a pasted link has no such
     * mark, and going back would leave the board; it closes onto the board instead.
     */
    const openedFrom = (location.state as { cardParent?: string } | null)?.cardParent;
    if (openedFrom === parent && window.history.state?.idx > 0) navigate(-1);
    else navigate(parent, { replace: true });
  };

  return (
    <>
      {/* M1.B1 §7.3–§7.6: the real header and the real view-tabs bar. */}
      <BoardHeader boardId={boardId} viewId={viewId ?? null} />
      <ViewTabsBar
        boardId={boardId}
        activeViewId={implicitDefaultView ? null : (viewId ?? null)}
        onSelectView={(next) => navigate(boardViewUrl(boardId, next))}
      />

      {/** M1.B4: the table view, driven by a `BoardDataSource` — the tRPC one on this route. */}
      {/* P-016: the log reserves its 700 px instead of covering the grid (`item-card.css`). */}
      <div className="ez-board-host" data-testid="board-view" data-log-open={activityLogOpen || undefined}>
        {/**
         * **P-140.** The board room for every **non-table** view kind, in one place. The table
         * joins it from its own provider (`TableViewProvider live`), which reconciles patches
         * into its store; a kanban / calendar / gantt / timeline / gallery / workload holds no
         * store and re-reads instead, so the room is bound beside the *source* and pushed into
         * it (`BoardViewRealtime`). Exactly one of the two is mounted at a time, because
         * `joinBoard`/`leaveBoard` are not reference-counted.
         */}
        {activeKind !== null && boardSource !== null ? (
          <BoardViewRealtime source={boardSource} boardId={boardId} viewId={viewId ?? null} />
        ) : null}
        {/**
         * M4.V1 R2: a view whose kind we have not built renders its *coming soon* screen, and a
         * **form** view renders the builder — never the table pretending to be either.
         */}
        {activeKind !== null && activeKind.registryId === 'form' ? (
          <BoardFormView boardId={boardId} viewId={viewId!} />
        ) : activeKind !== null && activeKind.registryId === 'dashboard' ? (
          /** M7 D1: the widget canvas — this account's five widget views land here. */
          <BoardWidgetView boardId={boardId} viewId={viewId!} />
        ) : activeKind !== null && activeKind.registryId === 'kanban' && boardSource !== null ? (
          /** M4 V2: lanes are group-by buckets, drag writes one cell (`specs/M4-V2-kanban.md`). */
          <KanbanView boardId={boardId} viewId={viewId!} source={boardSource} />
        ) : activeKind !== null && activeKind.registryId === 'calendar' && boardSource !== null ? (
          <CalendarView boardId={boardId} viewId={viewId!} source={boardSource} />
        ) : activeKind !== null && activeKind.registryId === 'gallery' && boardSource !== null ? (
          <GalleryView boardId={boardId} viewId={viewId!} source={boardSource} />
        ) : activeKind !== null && activeKind.registryId === 'gantt' && boardSource !== null ? (
          /** M4 V3: a bar's left edge is a date and dragging it writes one (`specs/M4-V3-gantt.md`). */
          <GanttView boardId={boardId} viewId={viewId!} source={boardSource} kind="gantt" />
        ) : activeKind !== null && activeKind.registryId === 'timeline' && boardSource !== null ? (
          <TimelineView boardId={boardId} viewId={viewId!} source={boardSource} />
        ) : activeKind !== null && activeKind.registryId === 'workload' && boardSource !== null ? (
          <WorkloadView boardId={boardId} viewId={viewId!} source={boardSource} />
        ) : activeKind !== null && !activeKind.implemented ? (
          <ComingSoonView
            boardId={boardId}
            title={activeKind.title}
            plannedIn={activeKind.plannedIn}
            mondayType={activeKind.mondayType}
          />
        ) : boardSource === null ? (
          <PageSkeleton variant="board" />
        ) : (
        <TableViewProvider
          source={boardSource}
          boardId={boardId}
          viewId={(viewId ?? 'main') as ViewId}
          live
        >
          <TableView
            activeViewId={(viewId ?? 'main') as ViewId}
            showBoardHeader={false}
            showViewTabs={false}
            onSelectView={(next) => navigate(boardViewUrl(boardId, next))}
            /*
             * **P-266.** The row's conversation bubble asks for the card's **Updates** tab.
             * The tab travels in the history entry's `state`, not in the URL: monday's deep
             * link is `/boards/<b>/pulses/<i>` and nothing else, and a migrated link must keep
             * working (§7.1), so no query string may be invented for it. A reload therefore
             * falls back to the card's own default, which is Updates anyway.
             */
            onOpenItem={(openedItemId, options) =>
              navigate(`${boardViewUrl(boardId, viewId ?? 'main')}/pulses/${openedItemId}`, {
                state: {
                  cardParent: boardViewUrl(boardId, viewId ?? ''),
                  ...(options?.tab ? { itemCardTab: options.tab } : {}),
                },
              })
            }
          />
        </TableViewProvider>
        )}
      </div>

      {/* B3 fills the bar; F4 owns the slot. */}
      <BottomActionBar
        open={selection.length > 0}
        summary={`${selection.length} selected`}
        data-testid="bulk-bar"
      />

      {/*
        R3: reloading `…/pulses/:itemId` reopens the panel with the board mounted underneath.
        R5: the placeholder `SidePanel` is gone — this is the real card, drawn to the captured
        700 × 812 geometry (`specs/M1-B7-item-card-and-updates.md` §2.3).
      */}
      {isValidId(itemId) && cardSource ? (
        <ItemCard
          source={cardSource}
          itemId={itemId}
          initialTab={
            (location.state as { itemCardTab?: ItemCardTab } | null)?.itemCardTab ?? 'updates'
          }
          onClose={() => closeSegment(boardViewUrl(boardId, viewId ?? ''))}
        />
      ) : null}

      {/* R5: the board log, 700 × 730, with the faceted `Filter log` panel (M1-B8 §2.3). */}
      {activityLogOpen && cardSource ? (
        <BoardActivityPanel
          source={cardSource}
          boardId={boardId}
          boardName={board.data?.name ?? 'Board'}
          onClose={() => closeSegment(boardViewUrl(boardId, viewId ?? ''))}
        />
      ) : null}

    </>
  );
}

/** R4: `/my_inbox` renders over the previous route; Escape returns to it. */
export function InboxRoute() {
  const navigate = useNavigate();
  const [tab, setTab] = usePreference('inbox.tab');
  const trpcClient = useTRPCClient();
  const source = useMemo(
    () => createNotificationsSource(trpcClient as unknown as NotificationsTrpc),
    [trpcClient],
  );
  const { revision } = useNotificationsRealtime(true);
  // P-283: `Mark all as read` has to empty the top bar's counter *now*, not when its 15 s
  // `staleTime` lapses. The badge reads `inbox.unreadCount`; this invalidates exactly that.
  const queryClient = useQueryClient();
  const unreadKeys = useUnreadQueryKeys();
  const onReadStateChange = useCallback(
    () => invalidateUnreadCounts(queryClient, unreadKeys),
    [queryClient, unreadKeys],
  );

  const close = useCallback(() => {
    // §7.1 rule b: go back when the previous entry is ours, otherwise replace with `/` (R4).
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate('/', { replace: true });
  }, [navigate]);

  /* P-325: a breadcrumb link leaves the feed for the board / item, in-app (no reload). */
  const openFromFeed = useCallback((href: string) => navigate(href), [navigate]);

  // The modal is a **route**, not an overlay, so it registers its own Escape against the one
  // registry rather than adding a raw `keydown` listener (§6, §7.9).
  useShortcut('escape', close);

  return (
    <div
      className="ez-dialog-backdrop"
      role="presentation"
      onClick={close}
      style={{ paddingBlockStart: 'var(--space-16)' }}
      data-testid="inbox-backdrop"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Update feed"
        onClick={(event) => event.stopPropagation()}
      >
        {/* M5.C3 owns the content; F4 keeps the backdrop, the Escape shortcut and the route. */}
        <InboxScreen
          source={source}
          tab={(tab as InboxTabId) ?? 'all_updates'}
          onTabChange={(next) => setTab(next)}
          onClose={close}
          revision={revision}
          onReadStateChange={onReadStateChange}
          onNavigate={openFromFeed}
        />
      </div>
    </div>
  );
}

/** Profile ▸ Notifications (M5 §6.1). */
export function NotificationSettingsRoute() {
  const trpcClient = useTRPCClient();
  const source = useMemo(
    () => createNotificationsSource(trpcClient as unknown as NotificationsTrpc),
    [trpcClient],
  );
  /**
   * **P-321** — the Tasks section's `tasks.settings` get/set, through the Tasks module's own
   * source. No new procedure and no second contract: this is the same port Tasks ▸ Preferences
   * writes through, which is what makes the two surfaces one store.
   */
  const tasksSource = useMemo(() => createTasksSource(trpcClient as unknown as TasksTrpc), [trpcClient]);
  return <NotificationSettings source={source} tasks={tasksSource} />;
}

/**
 * M5.C3 — `/my_work`.
 *
 * **The route moved.** monday's `/my_week` now answers "We couldn't find the content you were
 * looking for"; My Work lives at `/my_work` (`discovery/ui-reference/my-work/01-my-work.json`).
 * `/my_week` stays registered as a redirect, because doc 00's rule is that a migrated link
 * keeps working — monday 404ing its own legacy path is a regression, not a contract
 * (`specs/M7-C3-inbox-my-work.md` D-1).
 */
export function MyWorkRoute() {
  const navigate = useNavigate();
  const trpcClient = useTRPCClient();
  const source = useMemo(() => createMyWorkSource(trpcClient as unknown as MyWorkTrpc), [trpcClient]);
  return (
    <MyWorkPage
      source={source}
      onOpenItem={(row) => navigate(`/boards/${row.boardId}/pulses/${row.itemId}`)}
    />
  );
}

/**
 * `/tasks`, `/tasks/assigned`, `/tasks/archive`, `/tasks/insights`, `/tasks/timeline` (M10).
 *
 * One screen, five routes: the tab lives in the URL so a link to somebody's Archive is a link,
 * and `tabFromPath` is the only place that knows the mapping. Realtime is wired here rather than
 * inside the page, so the page stays renderable in the fixture harness with no socket.
 *
 * **P-319** · and five more, `…/pulses/:itemId`, one per tab. Clicking a row used to navigate to
 * `/boards/<b>/views/<v>/pulses/<i>`, which opened the card on the **board** and took the Tasks
 * list, its filters, its selection and its scroll with it. The card now opens over this page: the
 * URL gains the item, the list stays mounted behind the panel, and the only way out of Tasks is the
 * card header's `Open board ↗`.
 */
export function TasksRoute() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const trpcClient = useTRPCClient();
  const source = useMemo(() => createTasksSource(trpcClient as unknown as TasksTrpc), [trpcClient]);
  /** P-319 · the same `ItemCardSource` the board route builds; the card is the board's card. */
  const cardSource = useItemCardSource();
  const tab = tabFromPath(location.pathname);
  /**
   * **P-319** · the open item, from `…/pulses/:itemId`. Validated like every other id in this file
   * (R1): a non-numeric segment draws no card rather than asking the api about it.
   */
  const openItemId = isValidId(params['itemId']) ? (params['itemId'] ?? null) : null;
  /**
   * The board of the open item, carried in the history entry's `state` by the gesture that opened
   * it — the same trick P-266 uses for the card's initial tab, and for the same reason: monday's
   * deep link is `pulses/<itemId>` and nothing else (§7.1), so no board may be invented in the URL
   * for it. It buys one thing — the open card's board room, through which a rename made *in* the
   * card repaints the row underneath live. Without it (a cold deep link, a blocked websocket) the
   * page re-reads when the card closes instead, which `TasksPage` does unconditionally.
   */
  const cardBoardId =
    openItemId === null ? null : ((location.state as { taskBoardId?: string } | null)?.taskBoardId ?? null);
  const { revision } = useTasksRealtime(true, cardBoardId);

  /** §7.1 rule b: go back when the previous entry is ours, otherwise replace with the tab. */
  const closeCard = useCallback(() => {
    const openedFrom = (location.state as { cardParent?: string } | null)?.cardParent;
    if (openedFrom === TASK_TAB_PATHS[tab] && window.history.state?.idx > 0) navigate(-1);
    else navigate(TASK_TAB_PATHS[tab], { replace: true });
  }, [navigate, tab, location.state]);

  return (
    <TasksPage
      source={source}
      tab={tab}
      revision={revision}
      onTabChange={(next) => navigate(TASK_TAB_PATHS[next])}
      openItemId={openItemId}
      itemCardSource={cardSource}
      onOpenItem={(ref) =>
        /*
         * P-322: switching from one open card to another **replaces** the history entry, so the
         * list ever has one card entry on top of it and ✕ / Back return to the list — not to the
         * previously opened task (ten opens used to need ten closes).
         */
        navigate(taskItemUrl(tab, ref.itemId), {
          state: { taskBoardId: ref.boardId, cardParent: TASK_TAB_PATHS[tab] },
          replace: openItemId !== null,
        })
      }
      onCloseItem={closeCard}
      /* The one gesture that *does* leave: `Open board ↗` in the card's header. */
      onOpenBoard={(ref) => navigate(`/boards/${ref.boardId}/pulses/${ref.itemId}`)}
    />
  );
}

/** `/my_week` → `/my_work`, with `replace` so it leaves no history entry (C3-A11). */
export function MyWeekRedirect() {
  return <Navigate to="/my_work" replace />;
}

/** Routes that are registered so their URL keeps working, but whose module has not shipped. */
export function NotAvailableYet({ what, module }: { what: string; module: string }) {
  return (
    <div className="ez-page" data-testid="not-available-yet">
      <Heading type="h2" element="h1">
        {what}
      </Heading>
      <Text type="text2" className="ez-page__hint">
        {`${what} arrives with ${module}. The URL is registered now so links keep working.`}
      </Text>
    </div>
  );
}
